import Axios, { AxiosRequestConfig } from "axios";
import {
  getLoginUrl,
  getStoredAccessToken,
  refreshToken,
  removeAllTokens,
  ROUTES,
} from "~/helpers";
import { useAuthStore } from "~/stores/authStore";

export const axiosInstance = Axios.create();

// Flag to prevent multiple redirects
let isRedirecting = false;

// Paths that belong to AuthLayout where we should not perform token checks/redirects
const AUTH_PATH_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/change-password",
  "/auth/callback",
  "/transcribing",
  "/share",
  "/impersonate",
];

const isAuthRoute = (pathname: string) =>
  AUTH_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

axiosInstance.interceptors.request.use((config) => {
  const token = getStoredAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isOnAuthRoute =
      typeof location !== "undefined" && isAuthRoute(location.pathname);

    if (error?.response?.status === 406) {
      removeAllTokens();
      const { setAuthExpired, isImpersonating } = useAuthStore.getState();
      setAuthExpired(true);
      if (isImpersonating) {
        const { setImpersonating } = useAuthStore.getState();
        setImpersonating(false);
      }
      window.location.href = ROUTES.LOGIN;
      return Promise.reject(error);
    }
    if (error?.response?.status === 401 && !originalRequest._retry) {
      // Do not attempt refresh or redirect when on AuthLayout routes
      if (isOnAuthRoute) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      // impersonation tokens have no refresh token, go straight to logout
      const { isImpersonating } = useAuthStore.getState();
      if (isImpersonating) {
        removeAllTokens();
        const { setAuthExpired, setImpersonating } = useAuthStore.getState();
        setAuthExpired(true);
        setImpersonating(false);

        if (!isRedirecting) {
          isRedirecting = true;
          window.location.href = getLoginUrl();
        }
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await refreshToken({ force: true });
        if (newAccessToken) {
          return axiosInstance(originalRequest);
        } else {
          removeAllTokens();
          const { setAuthExpired } = useAuthStore.getState();
          setAuthExpired(true);

          if (isOnAuthRoute || isRedirecting) {
            return Promise.reject(error);
          }

          isRedirecting = true;
          window.location.href = getLoginUrl();
        }
      } catch (refreshError) {
        removeAllTokens();
        const { setAuthExpired } = useAuthStore.getState();
        setAuthExpired(true);

        if (!isOnAuthRoute && !isRedirecting) {
          isRedirecting = true;
          window.location.href = getLoginUrl();
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Adds Authorization header (if token exists) and returns response data
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return axiosInstance({
    ...config,
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
      ...options?.headers,
    },
  }).then(({ data }) => data as T);
};
